import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';
import { SnackbarService } from '../../core/services';
import { MeetingApi } from './meeting.api';
import * as MeetingActions from './meeting.actions';

@Injectable()
export class MeetingEffects {
  private readonly actions$ = inject(Actions);
  private readonly meetingApi = inject(MeetingApi);
  private readonly snackbar = inject(SnackbarService);

  startChannelMeeting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingActions.MeetingStartChannelRequest),
      switchMap(({ payload }) =>
        this.meetingApi.startChannelMeeting(payload).pipe(
          map((response) => MeetingActions.MeetingStartChannelSuccess({
            session: response.data,
            message: response.message,
          })),
          catchError((error) => of(MeetingActions.MeetingStartChannelFailure({ error: error.error?.message || error.message || 'Unknown error' })))
        )
      )
    )
  );

  answerChannelMeeting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingActions.MeetingAnswerChannelRequest),
      switchMap(({ payload }) =>
        this.meetingApi.answerChannelMeeting(payload).pipe(
          map((response) => MeetingActions.MeetingAnswerChannelSuccess({
            session: response.data,
            message: response.message,
          })),
          catchError((error) => of(MeetingActions.MeetingAnswerChannelFailure({ error: error.error?.message || error.message || 'Unknown error' })))
        )
      )
    )
  );

  joinChannelMeeting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingActions.MeetingJoinChannelRequest),
      switchMap(({ payload }) =>
        this.meetingApi.joinChannelMeeting(payload).pipe(
          map((response) => MeetingActions.MeetingJoinChannelSuccess({
            session: response.data,
            message: response.message,
          })),
          catchError((error) => of(MeetingActions.MeetingJoinChannelFailure({ error: error.error?.message || error.message || 'Unknown error' })))
        )
      )
    )
  );

  getChannelMeetingStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingActions.MeetingGetChannelStatusRequest),
      switchMap(({ payload }) =>
        this.meetingApi.getChannelMeetingStatus(payload).pipe(
          map((response) => MeetingActions.MeetingGetChannelStatusSuccess({
            status: response.data,
          })),
          catchError((error) => of(MeetingActions.MeetingGetChannelStatusFailure({
            channelId: payload.channel_id,
            error: error.error?.message || error.message || 'Unknown error',
          })))
        )
      )
    )
  );

  declineChannelMeeting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingActions.MeetingDeclineChannelRequest),
      switchMap(({ payload }) =>
        this.meetingApi.declineChannelMeeting(payload).pipe(
          map((response) => MeetingActions.MeetingDeclineChannelSuccess({
            channelId: payload.channel_id,
            message: response.message,
          })),
          catchError((error) => of(MeetingActions.MeetingDeclineChannelFailure({ error: error.error?.message || error.message || 'Unknown error' })))
        )
      )
    )
  );

  endChannelMeeting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingActions.MeetingEndChannelRequest),
      switchMap(({ payload }) =>
        this.meetingApi.endChannelMeeting(payload).pipe(
          map((response) => MeetingActions.MeetingEndChannelSuccess({
            channelId: payload.channel_id,
            message: response.message,
          })),
          catchError((error) => of(MeetingActions.MeetingEndChannelFailure({ error: error.error?.message || error.message || 'Unknown error' })))
        )
      )
    )
  );

  startChannelMeetingFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingActions.MeetingStartChannelFailure),
        map(({ error }) => {
          this.snackbar.showNotification('error', '', `Failed to start call: ${error}`, 'Close');
        })
      ),
    { dispatch: false }
  );

  answerChannelMeetingFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingActions.MeetingAnswerChannelFailure),
        map(({ error }) => {
          this.snackbar.showNotification('error', '', `Failed to answer call: ${error}`, 'Close');
        })
      ),
    { dispatch: false }
  );

  joinChannelMeetingFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingActions.MeetingJoinChannelFailure),
        map(({ error }) => {
          this.snackbar.showNotification('error', '', `Failed to join call: ${error}`, 'Close');
        })
      ),
    { dispatch: false }
  );

  declineChannelMeetingFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingActions.MeetingDeclineChannelFailure),
        map(({ error }) => {
          this.snackbar.showNotification('error', '', `Failed to decline call: ${error}`, 'Close');
        })
      ),
    { dispatch: false }
  );

  endChannelMeetingFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingActions.MeetingEndChannelFailure),
        map(({ error }) => {
          this.snackbar.showNotification('error', '', `Failed to end call: ${error}`, 'Close');
        })
      ),
    { dispatch: false }
  );
}
